import {
  NOTIFICATION_EVENT_TYPES,
  NOTIFICATION_BADGE_TEXTS,
  NOTIFICATION_TONES
} from '@/constants/notification-constants';

/*
 * Apresentação de notificação: rótulo, ícone e TOM semântico do evento.
 *
 * O tom é resolvido em CSS pelos tokens `--tc-*` (classe `is-<tom>`), não por
 * nome de cor do Vuetify. Antes cada mapa devolvia string Material
 * ('deep-purple', 'blue lighten-3') — cor fixa que ignora o tenant e perde
 * contraste no tema escuro.
 *
 * Usado pelo painel do sino e pela tela de histórico; as duas superfícies
 * precisam falar a mesma língua visual.
 */

const LABEL_MAP = {
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA]: NOTIFICATION_BADGE_TEXTS.PRE_VENDA,
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_UPPER]: NOTIFICATION_BADGE_TEXTS.PRE_VENDA,
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_EXTERNA]: NOTIFICATION_BADGE_TEXTS.OPERACAO_EXTERNA,
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_CLIENTE]: NOTIFICATION_BADGE_TEXTS.OPERACAO_CLIENTE,
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_DOLSPAY]: NOTIFICATION_BADGE_TEXTS.OPERACAO_DOLSPAY,
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA]: NOTIFICATION_BADGE_TEXTS.FICHA_ASSINADA,
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA_UPPER]: NOTIFICATION_BADGE_TEXTS.FICHA_ASSINADA,
  [NOTIFICATION_EVENT_TYPES.RECEIVER_ENVIADO]: NOTIFICATION_BADGE_TEXTS.RECEIVER_ENVIADO,
  [NOTIFICATION_EVENT_TYPES.RECEIVER_APROVADO]: NOTIFICATION_BADGE_TEXTS.RECEIVER_APROVADO,
  [NOTIFICATION_EVENT_TYPES.RECEIVER_PENDENTE]: NOTIFICATION_BADGE_TEXTS.RECEIVER_PENDENTE,
  [NOTIFICATION_EVENT_TYPES.RECEIVER_FALHA]: NOTIFICATION_BADGE_TEXTS.RECEIVER_FALHA,
  [NOTIFICATION_EVENT_TYPES.DUIMP_ATUALIZADO]: NOTIFICATION_BADGE_TEXTS.DUIMP_ATUALIZADO,
  [NOTIFICATION_EVENT_TYPES.STATUS_BANCARIO]: NOTIFICATION_BADGE_TEXTS.STATUS_BANCARIO,
  [NOTIFICATION_EVENT_TYPES.RFI_COMPLIANCE]: NOTIFICATION_BADGE_TEXTS.RFI_COMPLIANCE,
  [NOTIFICATION_EVENT_TYPES.LIMITE_AUMENTO]: NOTIFICATION_BADGE_TEXTS.LIMITE_AUMENTO
};

const ICON_MAP = {
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_UPPER]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_EXTERNA]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_CLIENTE]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_DOLSPAY]: 'PhCurrencyBtc',
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA]: 'PhFiles',
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA_UPPER]: 'PhFiles',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_ENVIADO]: 'PhPaperPlaneTilt',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_APROVADO]: 'PhCheckCircle',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_PENDENTE]: 'PhWarningCircle',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_FALHA]: 'PhXCircle',
  [NOTIFICATION_EVENT_TYPES.DUIMP_ATUALIZADO]: 'PhClipboardText',
  [NOTIFICATION_EVENT_TYPES.STATUS_BANCARIO]: 'PhBank',
  [NOTIFICATION_EVENT_TYPES.RFI_COMPLIANCE]: 'PhShieldCheck',
  [NOTIFICATION_EVENT_TYPES.LIMITE_AUMENTO]: 'PhTrendUp'
};

/* Lista para filtro de tipo na tela de histórico. As variantes MAIÚSCULAS são
   legado de registros antigos e não entram como opção — o filtro manda as
   duas grafias juntas para não esconder o histórico de antes da normalização. */
const NOTIFICATION_TYPE_OPTIONS = [
  { value: NOTIFICATION_EVENT_TYPES.PRE_VENDA, text: NOTIFICATION_BADGE_TEXTS.PRE_VENDA },
  { value: NOTIFICATION_EVENT_TYPES.PRE_VENDA_EXTERNA, text: NOTIFICATION_BADGE_TEXTS.OPERACAO_EXTERNA },
  { value: NOTIFICATION_EVENT_TYPES.PRE_VENDA_CLIENTE, text: NOTIFICATION_BADGE_TEXTS.OPERACAO_CLIENTE },
  { value: NOTIFICATION_EVENT_TYPES.PRE_VENDA_DOLSPAY, text: NOTIFICATION_BADGE_TEXTS.OPERACAO_DOLSPAY },
  { value: NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA, text: NOTIFICATION_BADGE_TEXTS.FICHA_ASSINADA },
  { value: NOTIFICATION_EVENT_TYPES.RECEIVER_ENVIADO, text: NOTIFICATION_BADGE_TEXTS.RECEIVER_ENVIADO },
  { value: NOTIFICATION_EVENT_TYPES.RECEIVER_APROVADO, text: NOTIFICATION_BADGE_TEXTS.RECEIVER_APROVADO },
  { value: NOTIFICATION_EVENT_TYPES.RECEIVER_PENDENTE, text: NOTIFICATION_BADGE_TEXTS.RECEIVER_PENDENTE },
  { value: NOTIFICATION_EVENT_TYPES.RECEIVER_FALHA, text: NOTIFICATION_BADGE_TEXTS.RECEIVER_FALHA },
  { value: NOTIFICATION_EVENT_TYPES.DUIMP_ATUALIZADO, text: NOTIFICATION_BADGE_TEXTS.DUIMP_ATUALIZADO },
  { value: NOTIFICATION_EVENT_TYPES.STATUS_BANCARIO, text: NOTIFICATION_BADGE_TEXTS.STATUS_BANCARIO },
  { value: NOTIFICATION_EVENT_TYPES.RFI_COMPLIANCE, text: NOTIFICATION_BADGE_TEXTS.RFI_COMPLIANCE },
  { value: NOTIFICATION_EVENT_TYPES.LIMITE_AUMENTO, text: NOTIFICATION_BADGE_TEXTS.LIMITE_AUMENTO }
];

const LEGACY_ALIASES = {
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA]: [NOTIFICATION_EVENT_TYPES.PRE_VENDA_UPPER],
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA]: [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA_UPPER]
};

export default {
  data() {
    return {
      notificationTypeOptions: NOTIFICATION_TYPE_OPTIONS
    };
  },
  methods: {
    getNotificationTone(eventType) {
      return NOTIFICATION_TONES[eventType] || 'neutral';
    },

    getNotificationToneClass(eventType) {
      return `is-${this.getNotificationTone(eventType)}`;
    },

    getNotificationLabel(eventType) {
      return LABEL_MAP[eventType] || NOTIFICATION_BADGE_TEXTS.NOTIFICACAO;
    },

    getNotificationIcon(eventType) {
      return ICON_MAP[eventType] || 'PhBell';
    },

    /* Envia junto as grafias legadas do mesmo evento, senão filtrar por
       "Pré-venda" esconderia tudo que foi gravado como 'PRE_VENDA'. */
    expandNotificationTypes(values) {
      if (!values || !values.length) return [];

      return values.reduce((all, value) => all.concat(value, LEGACY_ALIASES[value] || []), []);
    }
  }
};
